import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash2,
  Shield,
  Mail,
  Calendar,
  Key,
  FileBarChart,
} from "lucide-react"
import type { User } from "@/types"
import { staffRoleLabel } from "@/lib/staff-roles"
import { staffAvatarImageUrl } from "@/lib/staff-avatar"
import { cn, formatDateHuman } from "@/lib/utils"

interface AdminStaffTableProps {
  data: (User & { has_pin?: boolean })[]
  onEdit: (user: User) => void
  onDelete: (user: User) => void
  onSetPin?: (user: User) => void
  isLoading?: boolean
  readOnly?: boolean
}

export function AdminStaffTable({
  data,
  onEdit,
  onDelete,
  onSetPin,
  isLoading = false,
  readOnly = false,
}: AdminStaffTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])

  // Progressive column hiding for small screens — applied to BOTH TableHead
  // and TableCell (house pattern, see AdminCustomersDirectory). Name, Role and
  // Actions stay visible at every width.
  const columnClasses: Record<string, string> = {
    email: "hidden md:table-cell",
    is_active: "hidden lg:table-cell",
    created_at: "hidden xl:table-cell",
    pin: "hidden lg:table-cell",
  }

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-red-100 text-red-800 hover:bg-red-200',
      manager: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
      inventory_manager: 'bg-teal-100 text-teal-800 hover:bg-teal-200',
      counter: 'bg-green-100 text-green-800 hover:bg-green-200',
      accountant: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
    }
    return colors[role.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "first_name",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 lg:px-3"
          >
            Name
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ row }) => {
        const user = row.original
        const displayName = `${user.first_name} ${user.last_name}`.trim() || user.username
        const src = staffAvatarImageUrl(user)
        return (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <img
                src={src}
                alt={displayName}
                width={40}
                height={40}
                className="h-10 w-10 rounded-full object-cover ring-2 ring-amber-100/90 bg-amber-50 shadow-sm"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const el = e.currentTarget
                  if (el.dataset.fallback === "1") return
                  el.dataset.fallback = "1"
                  el.src =
                    "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f600.png"
                }}
              />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate max-w-[160px] sm:max-w-[220px]">
                {user.first_name} {user.last_name}
              </div>
              <div className="text-sm text-muted-foreground truncate max-w-[160px] sm:max-w-[220px]">@{user.username}</div>
              {/* Email column is hidden below md — surface it under the name instead */}
              <div className="text-xs text-muted-foreground truncate max-w-[160px] md:hidden">{user.email}</div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: "email",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 lg:px-3"
          >
            <Mail className="mr-2 h-4 w-4" />
            Email
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ getValue, row }) => {
        const email = getValue() as string
        // F-RPT-N16 — flag report-email subscribers here rather than in their
        // own column. Who receives the day's sales figures is worth being able
        // to see at a glance without opening every staff record, and it belongs
        // next to the address it is actually sent to.
        const receivesReports = Boolean(row.original.email_reports_enabled)
        return (
          <div className="flex items-center gap-2">
            <span className="text-foreground truncate max-w-[220px]" title={email}>{email}</span>
            {receivesReports && (
              <Badge
                variant="secondary"
                className="shrink-0 gap-1 px-1.5 py-0 text-[10px] font-medium"
                title="Receives the Z report by email after every Day Close"
              >
                <FileBarChart className="h-3 w-3" />
                Reports
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: "role",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 lg:px-3"
          >
            <Shield className="mr-2 h-4 w-4" />
            Role
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ getValue }) => {
        const role = getValue() as string
        return (
          <Badge className={getRoleBadgeColor(role)}>
            {staffRoleLabel(role)}
          </Badge>
        )
      },
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean
        return (
          <Badge variant={isActive ? "default" : "secondary"}>
            <div className={`w-2 h-2 rounded-full mr-2 ${isActive ? 'bg-green-400' : 'bg-gray-400'}`} />
            {isActive ? "Active" : "Inactive"}
          </Badge>
        )
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2 lg:px-3"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Joined
            {isSorted === "asc" ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        )
      },
      cell: ({ getValue }) => {
        const date = getValue() as string
        return (
          <div className="text-foreground">
            {formatDateHuman(date)}
          </div>
        )
      },
    },
    {
      id: "pin",
      header: () => (
        <div className="flex items-center">
          <Key className="mr-2 h-4 w-4" />
          PIN
        </div>
      ),
      cell: ({ row }) => {
        const user = row.original as User & { has_pin?: boolean }
        const canHavePin = user.role === 'admin' || user.role === 'manager'
        if (!canHavePin) return <span className="text-muted-foreground">—</span>
        return (
          <div className="flex items-center gap-2">
            {(user as any).has_pin ? (
              <Badge variant="secondary" className="text-xs">
                <Key className="w-3 h-3 mr-1" /> ****
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">Not set</span>
            )}
            {!readOnly && onSetPin && (
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onSetPin(user)}>
                {(user as any).has_pin ? 'Change' : 'Set'}
              </Button>
            )}
          </div>
        )
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const user = row.original
        if (readOnly) {
          return <span className="text-sm text-muted-foreground">View only</span>
        }
        return (
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(user)}
              className="h-8 px-2 lg:px-3"
            >
              <Edit className="h-4 w-4" />
              <span className="sr-only lg:not-sr-only lg:ml-2">Edit</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(user)}
              className="h-8 px-2 lg:px-3 text-red-600 hover:text-red-700 hover:border-red-300"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only lg:not-sr-only lg:ml-2">Delete</span>
            </Button>
          </div>
        )
      },
    },
  ]

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  return (
    <div className="w-full min-w-0">
      <div className="overflow-x-auto rounded-md border bg-card [-webkit-overflow-scrolling:touch]">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className={cn("px-4", columnClasses[header.column.id])}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((col, j) => (
                    <TableCell key={j} className={columnClasses[(col as { accessorKey?: string }).accessorKey ?? col.id ?? ""]}>
                      <div className="h-4 bg-muted rounded animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="hover:bg-muted/50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={columnClasses[cell.column.id]}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground">No staff members found</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
